import React from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import type { CustomerSalesHistoryItem } from '../../../types/sales.types';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';
import { mapOrderStatus } from '../../common/status/statusMap';
import * as S from '../../sales/orders/ExchangeModal/ExchangeDrawer.styles';
import {
  formatMoneyBo,
  formatSalesHistoryDate,
  postSaleStatusChip,
} from './clientCreateDrawer.labels';
import {
  commercialSaleDateDiffersFromRegistration,
  formatCommercialSaleDate,
} from '../../../utils/sales/commercial-sale-date';
import { drawerSectionCardSx, drawerSectionTitleSx } from './clientCreateDrawer.styles';

export type ClientCreateDrawerSalesHistoryProps = {
  historyItems: CustomerSalesHistoryItem[];
  historyLoading: boolean;
  historyError: string | null;
  historyPage: number;
  historyTotal: number;
  historyTotalPages: number;
  onRetry: () => void;
  onGoToOrder: (orderId: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
};

const ClientCreateDrawerSalesHistory: React.FC<ClientCreateDrawerSalesHistoryProps> = ({
  historyItems,
  historyLoading,
  historyError,
  historyPage,
  historyTotal,
  historyTotalPages,
  onRetry,
  onGoToOrder,
  onPrevPage,
  onNextPage,
}) => {
  return (
    <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
      <S.SectionHeader>
        <BadgeIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
        <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
          Historial de ventas y postventa
        </Typography>
      </S.SectionHeader>
      <Box sx={{ p: 2.5 }}>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : historyError != null ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Reintentar
              </Button>
            }
          >
            {historyError}
          </Alert>
        ) : historyItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Este cliente aún no tiene ventas registradas en el historial.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {historyItems.map((item) => {
              const postSale = postSaleStatusChip(item.postSaleStatus);
              const orderStatusChip = mapOrderStatus(item.orderStatus);
              return (
                <Box
                  key={item.orderId}
                  sx={(theme) => ({
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : theme.palette.grey[50],
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        role="button"
                        tabIndex={0}
                        onClick={() => onGoToOrder(item.orderId)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onGoToOrder(item.orderId);
                          }
                        }}
                        sx={(theme) => ({
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.primary.light
                              : theme.palette.primary.main,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': {
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.main
                                : theme.palette.primary.dark,
                          },
                        })}
                      >
                        {SALES_ORDER_NUMBER_LABEL}: {item.orderCode}
                      </Typography>
                      {BRANCH_RECEIPT_NUMBERING_ENABLED && item.branchReceiptCode ? (
                        <Typography variant="body2" color="text.secondary">
                          {BRANCH_RECEIPT_NUMBER_LABEL}: {item.branchReceiptCode}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip
                      size="small"
                      label={postSale.label}
                      color={postSale.color}
                      variant={postSale.color === 'default' ? 'outlined' : 'filled'}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatCommercialSaleDate(item, 'es-ES', { includeTime: true })}
                    {commercialSaleDateDiffersFromRegistration(item) && (
                      <>
                        {' '}
                        <Box component="span" sx={{ opacity: 0.85 }}>
                          (registro: {formatSalesHistoryDate(item.createdAt)})
                        </Box>
                      </>
                    )}{' '}
                    •{' '}
                    {item.locationName != null && item.locationName !== ''
                      ? item.locationName
                      : 'Sin sucursal'}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">
                      Monto: <strong>{formatMoneyBo(item.totalAmount)}</strong>
                    </Typography>
                    <Chip
                      size="small"
                      label={orderStatusChip.label}
                      color={orderStatusChip.color}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              );
            })}

            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Página {historyPage} de {historyTotalPages} • {historyTotal} registro(s)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={historyPage <= 1}
                  onClick={onPrevPage}
                >
                  Anterior
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={historyPage >= historyTotalPages}
                  onClick={onNextPage}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Stack>
        )}
      </Box>
    </S.SectionBox>
  );
};

export default ClientCreateDrawerSalesHistory;
